<div class="flex-1 flex flex-col bg-neutral-1 relative min-h-0 h-full">
  @if (activeDiscussion()) {
    <!-- Chat Header -->
    <div class="p-4 border-b border-neutral-3 bg-white dark:bg-neutral-2 flex justify-between items-center">
      @if (participantProfileUrl(); as profileUrl) {
        <a
          [href]="profileUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="flex items-center gap-3 group no-underline text-inherit cursor-pointer hover:opacity-95 transition-all"
          matTooltip="Voir le profil public"
        >
          @if (participantAvatarUrl(); as avatarUrl) {
            <img
              [src]="avatarUrl"
              [alt]="activeParticipantName()"
              class="size-10 rounded-full object-cover border border-neutral-3 shadow-xs group-hover:ring-2 group-hover:ring-primary-9 transition-all"
            />
          } @else {
            <div
              class="size-10 rounded-full bg-linear-to-br from-primary-9 to-indigo-9 text-white flex items-center justify-center font-bold shadow-xs group-hover:ring-2 group-hover:ring-primary-9 transition-all"
            >
              {{ activeParticipantInitial() }}
            </div>
          }
          <div>
            <h2
              class="text-sm font-bold text-neutral-12 leading-none mb-1 group-hover:text-primary-11 transition-colors flex items-center gap-1.5"
            >
              <span>{{ activeParticipantName() }}</span>
              <mat-icon
                class="text-xs! w-3.5! h-3.5! text-neutral-8 group-hover:text-primary-11 transition-colors"
                >open_in_new</mat-icon
              >
            </h2>
            <span class="text-xs text-neutral-10 font-medium">{{ subtitle() }}</span>
          </div>
        </a>
      } @else if (clickableProfile()) {
        <button
          type="button"
          (click)="profileClick.emit()"
          class="flex items-center gap-3 group no-underline text-inherit cursor-pointer hover:opacity-95 transition-all bg-transparent border-0 p-0 text-left focus:outline-hidden"
          matTooltip="Voir le profil"
        >
          @if (participantAvatarUrl(); as avatarUrl) {
            <img
              [src]="avatarUrl"
              [alt]="activeParticipantName()"
              class="size-10 rounded-full object-cover border border-neutral-3 shadow-xs group-hover:ring-2 group-hover:ring-primary-9 transition-all"
            />
          } @else {
            <div
              class="size-10 rounded-full bg-linear-to-br from-primary-9 to-indigo-9 text-white flex items-center justify-center font-bold shadow-xs group-hover:ring-2 group-hover:ring-primary-9 transition-all"
            >
              {{ activeParticipantInitial() }}
            </div>
          }
          <div>
            <h2
              class="text-sm font-bold text-neutral-12 leading-none mb-1 group-hover:text-primary-11 transition-colors flex items-center gap-1.5"
            >
              <span>{{ activeParticipantName() }}</span>
              <mat-icon
                class="text-xs! w-3.5! h-3.5! text-neutral-8 group-hover:text-primary-11 transition-colors"
                >visibility</mat-icon
              >
            </h2>
            <span class="text-xs text-neutral-10 font-medium">{{ subtitle() }}</span>
          </div>
        </button>
      } @else {
        <div class="flex items-center gap-3">
          @if (participantAvatarUrl(); as avatarUrl) {
            <img
              [src]="avatarUrl"
              [alt]="activeParticipantName()"
              class="size-10 rounded-full object-cover border border-neutral-3 shadow-xs"
            />
          } @else {
            <div
              class="size-10 rounded-full bg-linear-to-br from-primary-9 to-indigo-9 text-white flex items-center justify-center font-bold"
            >
              {{ activeParticipantInitial() }}
            </div>
          }
          <div>
            <h2 class="text-sm font-bold text-neutral-12 leading-none mb-1">
              {{ activeParticipantName() }}
            </h2>
            <span class="text-xs text-neutral-10 font-medium">{{ subtitle() }}</span>
          </div>
        </div>
      }

      <div class="flex items-center gap-2">
        <ng-content select="[chat-header-actions]"></ng-content>
      </div>
    </div>

    @if (pinnedMessage()) {
      <div
        class="px-6 py-2 bg-primary-2 dark:bg-primary-9/10 border-b border-primary-3 flex justify-between items-center text-xs text-primary-11 shrink-0 select-none"
      >
        <div class="flex items-center gap-2 min-w-0">
          <mat-icon class="size-4 text-base flex items-center justify-center shrink-0">push_pin</mat-icon>
          <span class="font-semibold shrink-0">Message épinglé :</span>
          <button
            type="button"
            class="truncate italic text-left cursor-pointer hover:underline bg-transparent border-0 p-0 text-inherit focus:outline-none"
            (click)="scrollToMessage(pinnedMessage()?._id || $any(pinnedMessage())?.id || pinnedMessage())"
          >
            {{ pinnedMessage()?.text || 'Voir le message' }}
          </button>
        </div>
        <button
          mat-icon-button
          class="size-6 flex items-center justify-center text-neutral-10 hover:text-neutral-12"
          (click)="unpinMessage.emit()"
        >
          <mat-icon class="size-4 text-base flex items-center justify-center">close</mat-icon>
        </button>
      </div>
    }

    <!-- Messages List -->
    <div
      #chatMessagesContainer
      id="chat-messages-container"
      (scroll)="onScroll()"
      class="flex-1 overflow-y-auto p-6 bg-neutral-1/40 flex flex-col"
    >
      @if (isLoadingMessages()) {
        <div class="flex-1 flex justify-center items-center">
          <mat-spinner diameter="40"></mat-spinner>
        </div>
      } @else {
        @if (isLoadingMore()) {
          <div class="flex justify-center items-center py-2 shrink-0">
            <mat-spinner diameter="24"></mat-spinner>
          </div>
        }

        <div class="text-center my-4">
          <span
            class="bg-neutral-3 dark:bg-neutral-3/20 text-neutral-10 text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded-full border border-neutral-3"
          >
            Début de la conversation
          </span>
        </div>

        @for (msg of decoratedMessages(); track msg._id; let idx = $index) {
          @if (msg.dateHeader) {
            <div class="text-center my-4">
              <span
                class="bg-neutral-3 dark:bg-neutral-3/20 text-neutral-10 text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded-full border border-neutral-3"
              >
                {{ msg.dateHeader }}
              </span>
            </div>
          }
          @if (isActionMessage(msg)) {
            <!-- Centered Action Message -->
            <div class="my-3 flex justify-center px-4">
              <div
                class="max-w-md w-full px-4 py-2.5 rounded-2xl bg-neutral-2/90 dark:bg-neutral-2 border border-neutral-4/70 flex items-center justify-between gap-3 transition-all shadow-xs"
              >
                <div class="flex items-center gap-3 min-w-0 text-left">
                  <div
                    class="size-8 rounded-xl bg-primary-9/15 text-primary-11 flex items-center justify-center shrink-0"
                  >
                    <mat-icon class="text-lg!">{{ getActionIcon(msg) }}</mat-icon>
                  </div>
                  <div class="flex flex-col min-w-0">
                    <span class="text-xs font-semibold text-neutral-12 leading-snug">
                      {{ getActionMessageText(msg) }}
                    </span>
                    <div class="flex items-center gap-2 mt-0.5 flex-wrap">
                      <span class="text-[10px] text-neutral-10">
                        {{ (msg.createdAt | date: timeFormat()) ?? '' }}
                      </span>
                      @if (getActionDeadline(msg); as deadline) {
                        <aura-countdown
                          [targetDate]="deadline"
                          variant="inline"
                          size="xs"
                          prefix="• Réponse sous :"
                        ></aura-countdown>
                      }
                    </div>
                  </div>
                </div>

                <button
                  type="button"
                  mat-stroked-button
                  class="!py-1 !px-2.5 text-xs font-bold text-primary shrink-0 hover:bg-primary-9/10"
                  (click)="actionMessageClick.emit(msg)"
                >
                  <span>Voir</span>
                </button>
              </div>
            </div>
          } @else {
            <aura-chat-bubble
              [id]="'message-' + msg._id"
              [text]="msg.text"
              [isMe]="msg.isMe"
              [showAvatar]="(activeDiscussion()?.participants?.length ?? 0) > 2"
              [participantInitial]="activeParticipantInitial()"
              [isFirstInGroup]="idx === 0"
              [isLastInGroup]="msg.isLastInGroup"
              [isSameGroupAsPrevious]="msg.isSameGroupAsPrevious"
              [time]="(msg.createdAt | date: timeFormat()) ?? ''"
              [showDelete]="showDeletePipe()(msg, idx, decoratedMessages())"
              [replyToText]="msg.replyTo ? msg.replyTo.text : null"
              [replyToSenderName]="getReplyToSenderName(msg)"
              [isReplyingTo]="replyingTo()?._id === msg._id"
              [sending]="msg.sending || false"
              [failed]="msg.failed || false"
              [isReadByAll]="msg.isReadByAll || false"
              [errorMessage]="msg.errorMessage || null"
              (reply)="replyingTo.set(msg)"
              (replyToClick)="scrollToMessage(msg.replyTo?._id || msg.replyTo?.id || '')"
              (copyText)="copyMessageText.emit(msg)"
              (pin)="pinMessage.emit(msg)"
              (info)="infoMessage.emit(msg)"
              (delete)="deleteMessage.emit(msg)"
              (retry)="retryMessage.emit(msg)"
            ></aura-chat-bubble>
          }
        }
      }
    </div>

    <!-- Content projection for custom pending actions -->
    <ng-content select="[chat-pending-actions]"></ng-content>

    <!-- Built-in pending actions section (positioned above chat input) -->
    @for (action of allPendingActions(); track action.id || action.type) {
      <div
        [class]="
          'px-5 py-3 border-t flex flex-col sm:flex-row sm:items-center justify-between gap-3 shrink-0 transition-all ' +
          (action.severity === 'warning'
            ? 'bg-amber-2/80 dark:bg-amber-9/15 border-amber-4/60'
            : action.severity === 'success'
              ? 'bg-jade-2/80 dark:bg-jade-9/15 border-jade-4/60'
              : 'bg-primary-2/80 dark:bg-primary-9/15 border-primary-3/60')
        "
      >
        <div class="flex items-center gap-3 min-w-0">
          <div
            [class]="
              'size-9 rounded-xl flex items-center justify-center shrink-0 ' +
              (action.severity === 'warning'
                ? 'bg-amber-9/15 text-amber-11'
                : action.severity === 'success'
                  ? 'bg-jade-9/15 text-jade-11'
                  : 'bg-primary-9/15 text-primary-11')
            "
          >
            <mat-icon class="text-xl!">{{ action.icon || 'notifications_active' }}</mat-icon>
          </div>
          <div class="min-w-0 flex flex-col">
            <div class="flex items-center gap-2 flex-wrap mb-0.5">
              @if (action.title) {
                <span class="text-xs font-bold text-neutral-12 leading-tight">{{ action.title }}</span>
              }
              @if (action.deadline) {
                <aura-countdown
                  [targetDate]="action.deadline"
                  variant="badge"
                  size="xs"
                  [showProgress]="false"
                  prefix="Délai :"
                ></aura-countdown>
              }
            </div>
            <span class="text-xs text-neutral-11 leading-snug">{{ action.message }}</span>
          </div>
        </div>

        @if (action.actionLabel) {
          <button
            type="button"
            mat-flat-button
            class="primary !py-2 !px-3.5 font-bold text-xs shrink-0 whitespace-nowrap self-end sm:self-center"
            (click)="pendingActionClick.emit(action)"
          >
            <span>{{ action.actionLabel }}</span>
          </button>
        }
      </div>
    }

    <aura-chat-input
      [replyText]="replyingTo() ? replyingTo()!.text : null"
      [replySenderName]="replyingTo() ? (isMe(replyingTo()!) ? 'Vous' : activeParticipantName()) : null"
      (messageSent)="onSendFromInput($event)"
      (replyCleared)="replyingTo.set(null)"
      (replyClicked)="scrollToActiveReply()"
      (click)="onInputFocusOrClick()"
      (focusin)="onInputFocusOrClick()"
    ></aura-chat-input>
  } @else {
    <!-- Unselected State -->
    <ng-content select="[chat-unselected-state]">
      <div class="flex-1 flex flex-col items-center justify-center text-center p-8 text-neutral-10">
        <div
          class="size-20 rounded-full bg-white dark:bg-neutral-2 border border-neutral-3 flex items-center justify-center mb-4"
        >
          <mat-icon class="size-10 text-4xl text-neutral-8">forum</mat-icon>
        </div>
        <h2 class="text-xl font-bold text-neutral-12 mb-1">Vos messages</h2>
        <p class="text-sm max-w-sm">Sélectionnez une discussion à gauche pour commencer à échanger.</p>
      </div>
    </ng-content>
  }
</div>
